import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { ArrowLeft, LayoutDashboard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../../providers/LocaleProvider';
import { CatalogFlowHeaderProgressBar } from './CatalogFlowHeaderProgressBar';
import { LessonPageHero } from '../learning/LessonPageHero';

export interface CatalogFlowPageHeaderProps {
  title: string;
  subtitle: string;
  icon: LucideIcon;
  /** Kept for callers; LessonPageHero uses a fixed violet clay palette. */
  accentColor?: string;
  /** Progress fill accent (rose = quiz, amber = exercise). */
  guideAccent: 'rose' | 'amber';
  onBack: () => void;
  /** Opens the learner dashboard. Falls back to `onBack` when omitted. */
  onBackToDashboard?: () => void;
  statsDone: number;
  statsTotal: number;
  /** Progress bar label variant (reviewed vs passed). */
  progressVariant?: 'quiz' | 'exercise';
  /** Matches `data-tour-id` on the hero shell. */
  tourId?: string;
  /** Optional eyebrow above the title (e.g. section label). */
  eyebrow?: string;
}

const HERO_BACK_BTN_CLASS =
  'inline-flex h-8 w-11 items-center justify-center rounded-lg border border-violet-200/90 bg-white/70 text-violet-950 shadow-sm backdrop-blur-[2px] transition-all hover:border-violet-300 hover:bg-white/90 active:scale-[0.98] dark:border-violet-700/50 dark:bg-violet-950/40 dark:text-violet-50 dark:hover:border-violet-600/60 dark:hover:bg-violet-950/55 md:w-12';

const HERO_DASHBOARD_BTN_CLASS =
  'flex w-full items-center justify-center gap-2 rounded-xl border border-violet-200/90 bg-white/65 px-3 py-2 text-xs font-semibold text-violet-950 shadow-sm backdrop-blur-[2px] transition-all hover:border-violet-300 hover:bg-white/85 active:scale-[0.99] dark:border-violet-700/50 dark:bg-violet-950/35 dark:text-violet-50 dark:hover:border-violet-600/60 dark:hover:bg-violet-950/50 md:w-auto md:px-4 md:py-2.5 md:text-sm';

export const CatalogFlowPageHeader: React.FC<CatalogFlowPageHeaderProps> = ({
  title,
  subtitle,
  icon,
  guideAccent,
  onBack,
  onBackToDashboard,
  statsDone,
  statsTotal,
  progressVariant = 'exercise',
  tourId,
  eyebrow,
}) => {
  const { t } = useTranslation('learning');
  const { direction } = useAppLocale();
  const isRtl = direction === 'rtl';
  const progressAccent = guideAccent === 'rose' ? 'rose' : 'amber';

  return (
    <LessonPageHero
      {...(eyebrow ? { eyebrow } : {})}
      title={title}
      description={subtitle}
      icon={icon}
      {...(tourId ? { tourId } : {})}
      belowIcon={
        <button type="button" onClick={onBack} className={HERO_BACK_BTN_CLASS} aria-label={t('lesson.goBack')} title={t('lesson.goBack')}>
          <ArrowLeft className={`h-4 w-4 ${isRtl ? 'rotate-180' : ''}`} aria-hidden />
        </button>
      }
      progress={
        <CatalogFlowHeaderProgressBar
          key={`flow-progress-${statsDone}-${statsTotal}-${progressVariant}`}
          done={statsDone}
          total={statsTotal}
          accent={progressAccent}
          hasHeroImage={false}
          variant={progressVariant}
          className="!mt-0"
        />
      }
      action={
        <button type="button" onClick={onBackToDashboard ?? onBack} className={HERO_DASHBOARD_BTN_CLASS}>
          <LayoutDashboard className="h-3.5 w-3.5 md:h-4 md:w-4" aria-hidden />
          <span>{t('catalog.backToDashboard')}</span>
        </button>
      }
    />
  );
};
